import { createElement, Component, createRef } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import { themr } from '../react-css-themr'
import dissoc from 'ramda/src/dissoc'
import { RIPPLE } from '../identifiers'
import events from '../utils/events'
import prefixer from '../utils/prefixer'

const defaults = {
  centered: false,
  className: '',
  multiple: true,
  passthrough: true,
  spread: 2,
  theme: {},
}

const rippleFactory = (options = {}) => {
  const {
    centered: defaultCentered,
    className: defaultClassName,
    multiple: defaultMultiple,
    passthrough: defaultPassthrough,
    spread: defaultSpread,
    theme: defaultTheme,
    ...props
  } = { ...defaults, ...options }

  return ComposedComponent => {
    class RippledComponent extends Component {
      static propTypes = {
        children: PropTypes.node,
        disabled: PropTypes.bool,
        onMouseDown: PropTypes.func,
        onRippleEnded: PropTypes.func,
        onTouchStart: PropTypes.func,
        ripple: PropTypes.bool,
        rippleCentered: PropTypes.bool,
        rippleClassName: PropTypes.string,
        rippleMultiple: PropTypes.bool,
        rippleSpread: PropTypes.number,
        theme: PropTypes.shape({
          ripple: PropTypes.string,
          rippleActive: PropTypes.string,
          rippleRestarting: PropTypes.string,
          rippleWrapper: PropTypes.string,
        }),
      }

      state = {
        ripples: {},
      }

      rootRef = createRef()

      componentDidUpdate(prevProps, prevState) {
        // If a new ripple was just added, add a remove event listener to its animation
        if (Object.keys(prevState.ripples).length < Object.keys(this.state.ripples).length) {
          this.addRippleRemoveEventListener(this.getLastKey())
        }
      }

      componentWillUnmount() {
        // Remove document event listeners for ripple if they still exist
        Object.keys(this.state.ripples).forEach(key => {
          this.state.ripples[key].endRipple()
        })
      }

      /**
       * Find out a descriptor object for the ripple element being created depending on
       * the position where it was triggered and the component's dimensions.
       *
       * @param {Number} x Coordinate x in the viewport where ripple was triggered
       * @param {Number} y Coordinate y in the viewport where ripple was triggered
       * @return {Object} Descriptor element including position and size of the element
       */
      getDescriptor(x, y) {
        const node = this.rootRef.current
        if (!node) {
          console.warn('RippledComponent: rootRef is not attached to a DOM node.')
          return { left: 0, top: 0, width: 0 }
        }
        const { left, top, height, width } = node.getBoundingClientRect()
        const { rippleCentered: centered = defaultCentered, rippleSpread: spread = defaultSpread } =
          this.props
        return {
          left: centered ? 0 : x - left - width / 2,
          top: centered ? 0 : y - top - height / 2,
          width: width * spread,
        }
      }

      /**
       * Increments an internal counter and returns the next value as a string.
       * It is used to assign key references to new ripple elements.
       *
       * @return {String} Key to be assigned to a ripple.
       */
      getNextKey() {
        this.currentCount = this.currentCount ? this.currentCount + 1 : 1
        return `ripple${this.currentCount}`
      }

      /**
       * Return the last generated key for a ripple element. When there is only one ripple
       * and to get the reference when a ripple was just created.
       *
       * @return {String} The last generated ripple key.
       */
      getLastKey() {
        return `ripple${this.currentCount}`
      }

      /**
       * Variable to store the ripple references
       */
      rippleNodes = {}

      /**
       * Determine if a ripple should start depending if it's a touch event. For mobile both
       * touchStart and mouseDown are launched so in case of touch we should always trigger
       * but if it's not we should check if a touch was already triggered to decide.
       *
       * @param {Boolean} isTouch True in case a touch event triggered the ripple false otherwise.
       * @return {Boolean} True in case the ripple should trigger or false if it shouldn't.
       */
      rippleShouldTrigger(isTouch) {
        const shouldStart = isTouch ? true : !this.touchCache
        this.touchCache = isTouch
        return shouldStart
      }

      /**
       * Start a ripple animation on a specific point with touch or mouse events. First
       * decides if the animation should trigger. If the ripple is multiple or there is no
       * ripple present, it creates a new key. If it's a simple ripple and already exists,
       * it just restarts the current ripple. The animation happens in two state changes
       * to allow triggering via CSS.
       *
       * @param {Number} x Coordinate X on the screen where animation should start
       * @param {Number} y Coordinate Y on the screen where animation should start
       * @param {Boolean} isTouch Use events from touch or mouse.
       */
      animateRipple(x, y, isTouch) {
        const { rippleMultiple = defaultMultiple } = this.props
        if (this.rippleShouldTrigger(isTouch)) {
          const { left, top, width } = this.getDescriptor(x, y)
          const noRipplesActive = Object.keys(this.state.ripples).length === 0
          const key = rippleMultiple || noRipplesActive ? this.getNextKey() : this.getLastKey()
          const endRipple = this.addRippleDeactivateEventListener(isTouch, key)
          const initialState = {
            active: false,
            restarting: true,
            top,
            left,
            width,
            endRipple,
          }
          const runningState = { active: true, restarting: false }
          const ripples = { ...this.state.ripples, [key]: initialState }
          this.setState({ ripples }, () => {
            if (this.rippleNodes[key]) this.rippleNodes[key].offsetWidth
            this.setState({
              ripples: {
                ...this.state.ripples,
                [key]: { ...this.state.ripples[key], ...runningState },
              },
            })
          })
        }
      }

      /**
       * Add an event listener to the ripple element's transition end so that
       * once the animation ends, the ripple can be safely removed from the state.
       *
       * @param {String} rippleKey The key of the ripple to add the event listener.
       */
      addRippleRemoveEventListener(rippleKey) {
        const self = this
        const rippleNode = this.rippleNodes[rippleKey]
        if (!rippleNode) return

        const onOpacityEnd = function (e) {
          if (e.propertyName === 'opacity') {
            if (self.props.onRippleEnded) self.props.onRippleEnded(e)
            events.removeEventListenerOnTransitionEnded(rippleNode, onOpacityEnd)
            delete self.rippleNodes[rippleKey]
            self.setState({ ripples: dissoc(rippleKey, self.state.ripples) })
          }
        }

        events.addEventListenerOnTransitionEnded(rippleNode, onOpacityEnd)
      }

      /**
       * Add an event listener to the document needed to deactivate a ripple
       * and make it disappear.
       *
       * @param {Boolean} isTouch True in case the trigger was a touch event false otherwise.
       * @param {String} rippleKey It's a key to identify the ripple that should be deactivated.
       * @return {Function} Callback function that deactivates the ripple and removes the listener
       */
      addRippleDeactivateEventListener(isTouch, rippleKey) {
        const eventType = isTouch ? 'touchend' : 'mouseup'
        const endRipple = this.createRippleDeactivateCallback(eventType, rippleKey)
        document.addEventListener(eventType, endRipple)
        return endRipple
      }

      /**
       * Generates a function that can be called to deactivate a ripple and remove its finishing
       * event listener. It's generated because we need to store it to be called on unmount in case
       * the ripple is still running.
       *
       * @param {String} eventType Is the event type that can be touchend or mouseup
       * @param {String} rippleKey Is the key representing the ripple
       * @return {Function} Callback function that deactivates the ripple and removes the listener
       */
      createRippleDeactivateCallback(eventType, rippleKey) {
        const self = this
        return function endRipple() {
          document.removeEventListener(eventType, endRipple)
          self.setState({
            ripples: {
              ...self.state.ripples,
              [rippleKey]: { ...self.state.ripples[rippleKey], active: false },
            },
          })
        }
      }

      doRipple = () => {
        const { disabled = false, ripple = true } = this.props
        return !disabled && ripple
      }

      handleMouseDown = event => {
        if (this.props.onMouseDown) this.props.onMouseDown(event)
        if (this.doRipple()) {
          const { x, y } = events.getMousePosition(event)
          this.animateRipple(x, y, false)
        }
      }

      handleTouchStart = event => {
        if (this.props.onTouchStart) this.props.onTouchStart(event)
        if (this.doRipple()) {
          const { x, y } = events.getTouchPosition(event)
          this.animateRipple(x, y, true)
        }
      }

      renderRipple(key, className, { active, left, restarting, top, width }) {
        const scale = restarting ? 0 : 1
        const transform = `translate3d(${-width / 2 + left}px, ${-width / 2 + top}px, 0) scale(${scale})`
        const _className = cx(
          this.props.theme.ripple,
          {
            [this.props.theme.rippleActive]: active,
            [this.props.theme.rippleRestarting]: restarting,
          },
          className
        )
        return (
          <span
            key={key}
            data-react-toolbox='ripple'
            className={this.props.theme.rippleWrapper}
            {...props}>
            <span
              className={_className}
              ref={node => {
                if (node) this.rippleNodes[key] = node
              }}
              style={prefixer({ transform }, { width, height: width })}
            />
          </span>
        )
      }

      render() {
        const {
          children,
          disabled = false,
          ripple = true,
          onRippleEnded,
          rippleCentered = defaultCentered, // eslint-disable-line
          rippleClassName = defaultClassName,
          rippleMultiple = defaultMultiple, // eslint-disable-line
          rippleSpread = defaultSpread, // eslint-disable-line
          theme,
          ...other
        } = this.props
        const { ripples } = this.state
        const childRipples = Object.keys(ripples).map(key =>
          this.renderRipple(key, rippleClassName, ripples[key])
        )
        const childProps = {
          onMouseDown: this.handleMouseDown,
          onTouchStart: this.handleTouchStart,
          ...other,
          ref: this.rootRef,
        }
        const finalProps = defaultPassthrough ? { ...childProps, theme, disabled } : childProps

        return !ripple
          ? createElement(ComposedComponent, finalProps, children)
          : createElement(ComposedComponent, finalProps, [children, childRipples])
      }
    }

    return themr(RIPPLE, defaultTheme)(RippledComponent)
  }
}

export default rippleFactory
